import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_URL } from '../config';
import PageHeader from './PageHeader';

const FOLDER_COLORS = [
  '#3b82f6', '#ef4444', '#22c55e', '#f97316', '#8b5cf6',
  '#06b6d4', '#ec4899', '#eab308', '#14b8a6', '#64748b'
];
const FOLDER_ICONS = ['📁', '📂', '🗂️', '⭐', '🔒', '📋', '💼', '🏢', '📊', '🎯', '💡', '🔧', '📦', '🗃️', '📌'];

function formatBytes(bytes) {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function formatCountdown(expiresAt) {
  if (!expiresAt) return null;
  const diff = new Date(expiresAt) - Date.now();
  if (diff <= 0) return 'Expirováno';
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  if (h >= 1) return `${h}h ${m}m`;
  return `${m}m`;
}

function getMimeIcon(mimeType, name) {
  if (!mimeType && name) {
    const ext = name.split('.').pop().toLowerCase();
    if (['jpg','jpeg','png','gif','webp','svg'].includes(ext)) return '🖼️';
    if (['pdf'].includes(ext)) return '📄';
    if (['doc','docx'].includes(ext)) return '📝';
    if (['xls','xlsx','csv'].includes(ext)) return '📊';
    if (['zip','rar','7z'].includes(ext)) return '🗜️';
    if (['mp4','avi','mov'].includes(ext)) return '🎬';
    if (['mp3','wav'].includes(ext)) return '🎵';
    return '📎';
  }
  if (!mimeType) return '📎';
  if (mimeType.startsWith('image/')) return '🖼️';
  if (mimeType === 'application/pdf') return '📄';
  if (mimeType.includes('word') || mimeType.includes('document')) return '📝';
  if (mimeType.includes('sheet') || mimeType.includes('excel')) return '📊';
  if (mimeType.includes('zip') || mimeType.includes('compressed')) return '🗜️';
  if (mimeType.startsWith('video/')) return '🎬';
  if (mimeType.startsWith('audio/')) return '🎵';
  return '📎';
}

export default function CloudStorage({ currentUserId, userRole, isDarkMode }) {
  const isAdmin = userRole === 'admin' || userRole === 'superadmin';
  const borderCls = isDarkMode ? 'border-gray-700' : 'border-gray-200';

  // === TEMP FILES state ===
  const [tempFiles, setTempFiles] = useState([]);
  const [tempLoading, setTempLoading] = useState(false);
  const [tempUploading, setTempUploading] = useState(false);
  const tempInputRef = useRef();

  // === PERMANENT FILES state ===
  const [folders, setFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [permFiles, setPermFiles] = useState([]);
  const [permLoading, setPermLoading] = useState(false);
  const [permUploading, setPermUploading] = useState(false);
  const permInputRef = useRef();

  // === DRAG & DROP state ===
  const [tempDragOver, setTempDragOver] = useState(false);
  const [permDragOver, setPermDragOver] = useState(false);

  // === CONTEXT MENU state ===
  const [contextMenu, setContextMenu] = useState(null); // { x, y, folder }
  const [expandedFolders, setExpandedFolders] = useState(new Set());

  // Close context menu on outside click
  useEffect(() => {
    const close = () => setContextMenu(null);
    if (contextMenu) {
      document.addEventListener('click', close);
      return () => document.removeEventListener('click', close);
    }
  }, [contextMenu]);

  const handleFolderContextMenu = (e, folder) => {
    if (!isAdmin) return;
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, folder });
  };

  // Drag & drop handlers
  const handleTempDrop = async (e) => {
    e.preventDefault();
    setTempDragOver(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (!files.length) return;
    setTempUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('type', 'temp');
      fd.append('userId', currentUserId);
      try { await fetch(`${API_URL}/cloud/upload`, { method: 'POST', body: fd }); }
      catch (err) { console.error(err); }
    }
    setTempUploading(false);
    loadTempFiles();
  };

  const handlePermDrop = async (e) => {
    e.preventDefault();
    setPermDragOver(false);
    if (!isAdmin) return;
    const files = Array.from(e.dataTransfer.files || []);
    if (!files.length) return;
    setPermUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('type', 'permanent');
      fd.append('userId', currentUserId);
      if (selectedFolderId) fd.append('folderId', selectedFolderId);
      try { await fetch(`${API_URL}/cloud/upload`, { method: 'POST', body: fd }); }
      catch (err) { console.error(err); }
    }
    setPermUploading(false);
    loadPermFiles(selectedFolderId);
  };

  // === FOLDER MODAL ===
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [folderName, setFolderName] = useState('');
  const [folderColor, setFolderColor] = useState('#3b82f6');
  const [folderIcon, setFolderIcon] = useState('📁');

  // === COUNTDOWN timer ===
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(v => v + 1), 60000);
    return () => clearInterval(t);
  }, []);

  // Load temp files
  const loadTempFiles = useCallback(async () => {
    if (!currentUserId) return;
    setTempLoading(true);
    try {
      const res = await fetch(`${API_URL}/cloud/files?type=temp&userId=${currentUserId}`);
      const data = await res.json();
      setTempFiles(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
    setTempLoading(false);
  }, [currentUserId]);

  // Load folders
  const loadFolders = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/cloud/folders?userId=${currentUserId}`);
      const data = await res.json();
      setFolders(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
  }, [currentUserId]);

  // Load permanent files for selected folder
  const loadPermFiles = useCallback(async (folderId) => {
    setPermLoading(true);
    try {
      const folderParam = folderId ? `&folderId=${folderId}` : '&folderId=null';
      const res = await fetch(`${API_URL}/cloud/files?type=permanent&userId=${currentUserId}${folderParam}`);
      const data = await res.json();
      setPermFiles(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
    setPermLoading(false);
  }, [currentUserId]);

  useEffect(() => {
    loadTempFiles();
    loadFolders();
  }, [loadTempFiles, loadFolders]);

  useEffect(() => {
    loadPermFiles(selectedFolderId);
  }, [selectedFolderId, loadPermFiles]);

  // Upload temp file
  const handleTempUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setTempUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('type', 'temp');
      fd.append('userId', currentUserId);
      try {
        await fetch(`${API_URL}/cloud/upload`, { method: 'POST', body: fd });
      } catch (err) { console.error(err); }
    }
    setTempUploading(false);
    tempInputRef.current.value = '';
    loadTempFiles();
  };

  // Delete temp file
  const deleteTempFile = async (fileId) => {
    if (!window.confirm('Smazat soubor?')) return;
    await fetch(`${API_URL}/cloud/files/${fileId}?userId=${currentUserId}`, { method: 'DELETE' });
    loadTempFiles();
  };

  // Download file
  const downloadFile = (fileId) => {
    window.open(`${API_URL}/cloud/download/${fileId}`, '_blank');
  };

  // Rename file
  const [renamingFileId, setRenamingFileId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const startRename = (file) => {
    setRenamingFileId(file._id);
    setRenameValue(file.originalName);
  };

  const cancelRename = () => {
    setRenamingFileId(null);
    setRenameValue('');
  };

  const commitRename = async (file) => {
    const trimmed = renameValue.trim();
    if (!trimmed || trimmed === file.originalName) { cancelRename(); return; }
    try {
      await fetch(`${API_URL}/cloud/files/${file._id}/rename`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newName: trimmed, userId: currentUserId })
      });
      cancelRename();
      loadTempFiles();
      loadPermFiles(selectedFolderId);
    } catch (err) { console.error(err); }
  };

  // Upload permanent file
  const handlePermUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setPermUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('type', 'permanent');
      fd.append('userId', currentUserId);
      if (selectedFolderId) fd.append('folderId', selectedFolderId);
      try {
        await fetch(`${API_URL}/cloud/upload`, { method: 'POST', body: fd });
      } catch (err) { console.error(err); }
    }
    setPermUploading(false);
    permInputRef.current.value = '';
    loadPermFiles(selectedFolderId);
  };

  // Delete permanent file
  const deletePermFile = async (fileId) => {
    if (!window.confirm('Smazat soubor?')) return;
    await fetch(`${API_URL}/cloud/files/${fileId}?userId=${currentUserId}`, { method: 'DELETE' });
    loadPermFiles(selectedFolderId);
  };

  // Save folder
  const saveFolder = async () => {
    if (!folderName.trim()) return;
    const body = { name: folderName.trim(), color: folderColor, icon: folderIcon, userId: currentUserId };
    if (!editingFolder) body.parentId = selectedFolderId || null;
    if (editingFolder) {
      await fetch(`${API_URL}/cloud/folders/${editingFolder._id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
      });
    } else {
      await fetch(`${API_URL}/cloud/folders`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
      });
    }
    setShowFolderModal(false);
    setEditingFolder(null);
    setFolderName('');
    setFolderColor('#3b82f6');
    setFolderIcon('📁');
    loadFolders();
  };

  // Delete folder
  const deleteFolder = async (folderId) => {
    if (!window.confirm('Smazat složku a všechny její soubory?')) return;
    await fetch(`${API_URL}/cloud/folders/${folderId}?userId=${currentUserId}`, { method: 'DELETE' });
    if (selectedFolderId === folderId) setSelectedFolderId(null);
    loadFolders();
  };

  // Open edit folder modal
  const openEditFolder = (folder) => {
    setEditingFolder(folder);
    setFolderName(folder.name);
    setFolderColor(folder.color || '#3b82f6');
    setFolderIcon(folder.icon || '📁');
    setShowFolderModal(true);
  };

  const toggleFolder = (folderId) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(String(folderId))) next.delete(String(folderId));
      else next.add(String(folderId));
      return next;
    });
  };

  const getChildren = (parentId) => folders.filter(f =>
    parentId === null ? !f.parentId : String(f.parentId) === String(parentId)
  );

  const getFolderPath = (folderId) => {
    if (!folderId) return [];
    const folder = folders.find(f => String(f._id) === String(folderId));
    if (!folder) return [];
    return [...getFolderPath(folder.parentId), folder];
  };

  const renderFolderItem = (folder, depth = 0) => {
    const children = getChildren(folder._id);
    const hasChildren = children.length > 0;
    const isSelected = String(selectedFolderId) === String(folder._id);
    const isExpanded = expandedFolders.has(String(folder._id));
    return (
      <React.Fragment key={folder._id}>
        <button
          onClick={() => {
            setSelectedFolderId(folder._id);
            if (hasChildren && !isExpanded) toggleFolder(folder._id);
          }}
          onContextMenu={e => handleFolderContextMenu(e, folder)}
          className={`flex items-center gap-1.5 pr-3 py-2 text-sm font-medium transition text-left w-full ${
            isSelected
              ? 'text-white'
              : isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'
          }`}
          style={{
            paddingLeft: `${0.75 + depth * 0.875}rem`,
            ...(isSelected ? { backgroundColor: folder.color || '#3b82f6' } : {})
          }}
          title={isAdmin ? 'Pravý klik = upravit/smazat' : folder.name}
        >
          <span className="flex-shrink-0">{folder.icon || '📁'}</span>
          <span className="truncate">{folder.name}</span>
        </button>
        {hasChildren && isExpanded && children.map(child => renderFolderItem(child, depth + 1))}
      </React.Fragment>
    );
  };

  const inputCls = `px-3 py-2 rounded-lg border text-sm w-full ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'}`;
  const btnPrimary = `px-4 py-2 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white transition`;
  const btnDanger = `px-2 py-1 rounded text-xs bg-red-500 hover:bg-red-600 text-white transition`;
  const btnSecondary = `px-2 py-1 rounded text-xs bg-gray-500 hover:bg-gray-600 text-white transition`;

  return (
    <div className={`h-screen flex flex-col overflow-hidden ${isDarkMode ? 'bg-gradient-to-br from-gray-900 to-gray-800' : 'bg-gradient-to-br from-gray-100 to-blue-100'} py-4`}>
      <div className="flex-1 flex flex-col overflow-hidden min-h-0 w-full max-w-[1800px] mx-auto px-2 md:px-4">
      <PageHeader title="☁️ Cloud úložiště" isDarkMode={isDarkMode} />

      <div className="flex-1 overflow-hidden max-w-[1600px] w-full mx-auto px-4 py-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 h-full">

          {/* === LEVÝ PANEL - Dočasné soubory === */}
          <div
            className={`rounded-xl border shadow-sm flex flex-col overflow-hidden transition-colors ${tempDragOver ? 'border-blue-400 bg-blue-50/30 dark:bg-blue-900/20' : isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
            onDragOver={e => { e.preventDefault(); setTempDragOver(true); }}
            onDragLeave={() => setTempDragOver(false)}
            onDrop={handleTempDrop}
          >
            <div className={`p-4 border-b ${borderCls} flex items-center justify-between`}>
              <div>
                <h2 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  🕐 Moje dočasné soubory
                </h2>
                <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Soubory se automaticky smažou po 24 hodinách
                </p>
                <p className={`text-xs mt-0.5 ${tempDragOver ? 'text-blue-500 font-semibold' : isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {tempDragOver ? '⬇️ Pusťte soubory...' : 'Přetáhněte soubory sem nebo použijte tlačítko'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <input ref={tempInputRef} type="file" multiple className="hidden" onChange={handleTempUpload} />
                <button onClick={() => tempInputRef.current?.click()} disabled={tempUploading}
                  className={`${btnPrimary} flex items-center gap-1.5`}>
                  {tempUploading ? '⏳ Nahrávám...' : '⬆️ Nahrát'}
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {tempLoading ? (
                <div className={`text-center py-8 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Načítám...</div>
              ) : tempFiles.length === 0 ? (
                <div className={`text-center py-12 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  <div className="text-4xl mb-2">📭</div>
                  <div className="text-sm">Žádné dočasné soubory</div>
                  <div className="text-xs mt-1">Nahrajte soubory tlačítkem výše</div>
                </div>
              ) : (
                <div className="space-y-2">
                  {tempFiles.map(file => {
                    const countdown = formatCountdown(file.expiresAt);
                    const isExpiringSoon = file.expiresAt && (new Date(file.expiresAt) - Date.now()) < 3600000;
                    return (
                      <div key={file._id}
                        className={`flex items-center gap-3 p-3 rounded-lg border ${isDarkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                        <span className="text-2xl flex-shrink-0">{getMimeIcon(file.mimeType, file.originalName)}</span>
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`} title={file.originalName}>
                            {file.originalName}
                          </div>
                          <div className="flex items-center gap-3 mt-0.5">
                            <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{formatBytes(file.fileSize)}</span>
                            {countdown && (
                              <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${
                                isExpiringSoon
                                  ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                                  : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                              }`}>
                                ⏱ {countdown}
                              </span>
                            )}
                          </div>
                        </div>
                        {renamingFileId === file._id ? (
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <input
                              autoFocus
                              className={`text-sm px-2 py-1 rounded border w-40 ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                              value={renameValue}
                              onChange={e => setRenameValue(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter') commitRename(file); if (e.key === 'Escape') cancelRename(); }}
                            />
                            <button onClick={() => commitRename(file)} className={btnSecondary} title="Uložit">✅</button>
                            <button onClick={cancelRename} className={btnDanger} title="Zrušit">✕</button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <button onClick={() => downloadFile(file._id)} className={btnSecondary} title="Stáhnout">⬇️</button>
                            {isAdmin && <button onClick={() => startRename(file)} className={btnSecondary} title="Přejmenovat">✏️</button>}
                            <button onClick={() => deleteTempFile(file._id)} className={btnDanger} title="Smazat">🗑️</button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* === PRAVÝ PANEL - Firemní soubory === */}
          <div
            className={`rounded-xl border shadow-sm flex flex-col overflow-hidden transition-colors ${permDragOver && isAdmin ? 'border-green-400 bg-green-50/30 dark:bg-green-900/20' : isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
            onDragOver={e => { e.preventDefault(); if (isAdmin) setPermDragOver(true); }}
            onDragLeave={() => setPermDragOver(false)}
            onDrop={handlePermDrop}
          >
            <div className={`p-4 border-b ${borderCls} flex items-center justify-between`}>
              <div>
                <h2 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  🏢 Firemní soubory
                </h2>
                <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Trvalé soubory sdílené celou firmou
                </p>
                {isAdmin && (
                  <p className={`text-xs mt-0.5 ${permDragOver ? 'text-green-500 font-semibold' : isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    {permDragOver ? '⬇️ Pusťte soubory...' : 'Přetáhněte soubory sem nebo použijte tlačítko'}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {isAdmin && (
                  <>
                    <input ref={permInputRef} type="file" multiple className="hidden" onChange={handlePermUpload} />
                    <button onClick={() => permInputRef.current?.click()} disabled={permUploading}
                      className={`${btnPrimary} flex items-center gap-1.5`}>
                      {permUploading ? '⏳...' : '⬆️ Nahrát'}
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Folder sidebar + file area */}
            <div className="flex flex-1 min-h-0 overflow-hidden">

              {/* Vertikální sidebar složek */}
              <div className={`w-44 flex-shrink-0 border-r ${borderCls} flex flex-col overflow-y-auto`}>
                <div className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Složky
                </div>
                {/* Kořen */}
                <button
                  onClick={() => setSelectedFolderId(null)}
                  className={`flex items-center gap-2 px-3 py-2 text-sm font-medium transition text-left w-full ${
                    selectedFolderId === null
                      ? 'bg-blue-600 text-white'
                      : isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <span>🏠</span>
                  <span className="truncate">Hlavní složka</span>
                </button>
                {/* Složky - stromová struktura */}
                {getChildren(null).map(folder => renderFolderItem(folder))}
                {isAdmin && (
                  <button
                    onClick={() => { setEditingFolder(null); setFolderName(''); setFolderColor('#3b82f6'); setFolderIcon('📁'); setShowFolderModal(true); }}
                    className={`flex items-center gap-2 px-3 py-2 text-sm transition text-left w-full mt-auto border-t ${borderCls} ${isDarkMode ? 'text-green-400 hover:bg-gray-700' : 'text-green-600 hover:bg-gray-100'}`}
                  >
                    <span>➕</span>
                    <span>Nová složka</span>
                  </button>
                )}
              </div>

              {/* Soubory v aktuální složce */}
            <div className="flex-1 overflow-y-auto p-4">
              {/* Breadcrumb */}
              {selectedFolderId && (
                <div className={`flex items-center flex-wrap gap-1 pb-3 mb-3 border-b text-sm ${borderCls}`}>
                  <button onClick={() => setSelectedFolderId(null)} className={isDarkMode ? 'text-blue-400 hover:underline' : 'text-blue-600 hover:underline'}>
                    🏠 Hlavní
                  </button>
                  {getFolderPath(selectedFolderId).map((f) => (
                    <React.Fragment key={f._id}>
                      <span className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>/</span>
                      <button onClick={() => setSelectedFolderId(f._id)} className={isDarkMode ? 'text-blue-400 hover:underline' : 'text-blue-600 hover:underline'}>
                        {f.icon} {f.name}
                      </button>
                    </React.Fragment>
                  ))}
                </div>
              )}
              {permLoading ? (
                <div className={`text-center py-8 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Načítám...</div>
              ) : permFiles.length === 0 ? (
                <div className={`text-center py-12 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  <div className="text-4xl mb-2">📂</div>
                  <div className="text-sm">Žádné soubory{selectedFolderId ? ' v této složce' : ' v kořenu'}</div>
                  {isAdmin && <div className="text-xs mt-1">Nahrajte soubory tlačítkem výše</div>}
                </div>
              ) : (
                <div className="space-y-2">
                  {permFiles.map(file => (
                    <div key={file._id}
                      className={`flex items-center gap-3 p-3 rounded-lg border ${isDarkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                      <span className="text-2xl flex-shrink-0">{getMimeIcon(file.mimeType, file.originalName)}</span>
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`} title={file.originalName}>
                          {file.originalName}
                        </div>
                        <div className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {formatBytes(file.fileSize)} • {new Date(file.createdAt).toLocaleDateString('cs-CZ')}
                        </div>
                      </div>
                      {renamingFileId === file._id ? (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <input
                            autoFocus
                            className={`text-sm px-2 py-1 rounded border w-40 ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                            value={renameValue}
                            onChange={e => setRenameValue(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') commitRename(file); if (e.key === 'Escape') cancelRename(); }}
                          />
                          <button onClick={() => commitRename(file)} className={btnSecondary} title="Uložit">✅</button>
                          <button onClick={cancelRename} className={btnDanger} title="Zrušit">✕</button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <button onClick={() => downloadFile(file._id)} className={btnSecondary} title="Stáhnout">⬇️</button>
                          {isAdmin && <button onClick={() => startRename(file)} className={btnSecondary} title="Přejmenovat">✏️</button>}
                          {isAdmin && (
                            <button onClick={() => deletePermFile(file._id)} className={btnDanger} title="Smazat">🗑️</button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Context menu pro složky (pravý klik) */}
      {contextMenu && isAdmin && (
        <div
          className={`fixed z-[200] rounded-lg shadow-xl border py-1 min-w-[160px] ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onClick={e => e.stopPropagation()}
        >
          <div className={`px-4 py-1.5 text-xs font-semibold truncate border-b ${borderCls} ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {contextMenu.folder.icon} {contextMenu.folder.name}
          </div>
          <button
            onClick={() => { openEditFolder(contextMenu.folder); setContextMenu(null); }}
            className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 ${isDarkMode ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'} transition`}
          >
            ✏️ Upravit složku
          </button>
          <button
            onClick={() => { deleteFolder(contextMenu.folder._id); setContextMenu(null); }}
            className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition`}
          >
            🗑️ Smazat složku
          </button>
        </div>
      )}

      {/* Folder modal */}
      {showFolderModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className={`rounded-xl shadow-2xl w-full max-w-md p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {editingFolder ? '✏️ Upravit složku' : '➕ Nová složka'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className={`text-xs font-medium mb-1 block ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Název</label>
                <input value={folderName} onChange={e => setFolderName(e.target.value)} className={inputCls} placeholder="Název složky..." autoFocus />
              </div>
              <div>
                <label className={`text-xs font-medium mb-1 block ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Ikona</label>
                <div className="flex flex-wrap gap-2">
                  {FOLDER_ICONS.map(ico => (
                    <button key={ico} onClick={() => setFolderIcon(ico)}
                      className={`w-9 h-9 rounded-lg text-xl flex items-center justify-center transition ${folderIcon === ico ? 'ring-2 ring-blue-500 bg-blue-100 dark:bg-blue-900/40' : isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'}`}>
                      {ico}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className={`text-xs font-medium mb-1 block ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Barva</label>
                <div className="flex flex-wrap gap-2">
                  {FOLDER_COLORS.map(color => (
                    <button key={color} onClick={() => setFolderColor(color)}
                      className={`w-8 h-8 rounded-full transition ${folderColor === color ? 'ring-2 ring-offset-2 ring-gray-600' : ''}`}
                      style={{ backgroundColor: color }} />
                  ))}
                </div>
              </div>
              {/* Preview */}
              <div className={`flex items-center gap-2 p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <span className="text-2xl">{folderIcon}</span>
                <span className="font-medium text-sm" style={{ color: folderColor }}>{folderName || 'Náhled složky'}</span>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => { setShowFolderModal(false); setEditingFolder(null); }}
                className={`px-4 py-2 rounded-lg text-sm border ${borderCls} ${isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'} transition`}>
                Zrušit
              </button>
              <button onClick={saveFolder} disabled={!folderName.trim()}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white transition">
                {editingFolder ? 'Uložit' : 'Vytvořit'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

